// The board's own clipboard (qa-runs#1428): what the DM copied — the things' ids and where they stood — until the
// page is reloaded. Never the computer's clipboard. A paste is ONE `objects.paste` of at most `OBJECT_LIMITS.copies`
// (50, qa-runs#1446; the server holds the same cap), the group keeping its shape around the spot chosen, a copy whose
// square is taken going to the nearest free one (`placeSpot`), and the copies selected afterwards, ready to drag.
// A copy pastes on any scene of the table: on another, the paste names the scene it came from (`from`) and the server
// reads the things there; this browser holds only the scene it shows, so a source removed there is left out by it.
import { OBJECT_LIMITS, type EventBody, type Grid, type Point } from '@dragons-table/vtt-shared';
import { placeSpot } from '../place';
import { selection } from './selection';

interface Copied { scene: string | null; items: Array<{ source: string; x: number; y: number }> }
let copied: Copied | null = null;
const listeners = new Set<() => void>();

export const boardClipboard = {
  /** How many a paste would make, 0 when nothing is copied (or it can't name the scene it was copied on). */
  count(scene: string | null): number { return copied && pastesOn(copied, scene) ? Math.min(copied.items.length, OBJECT_LIMITS.copies) : 0; },
  copy(scene: string | null, things: Array<{ id: string; x: number; y: number }>): void {
    copied = things.length ? { scene, items: things.slice(0, OBJECT_LIMITS.copies).map((o) => ({ source: o.id, x: o.x, y: o.y })) } : null;
    for (const l of listeners) l();
  },
  subscribe(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** The paste at `at`, or null when there is nothing to paste here (a source since removed is left out). */
  paste(scene: string | null, at: Point, state: { grid: Grid; scene: { snap?: boolean }; objects: Record<string, { x: number; y: number }> }, extent: { width: number; height: number }): EventBody | null {
    if (!copied || !pastesOn(copied, scene)) return null;
    const here = copied.scene === scene;
    const items = here ? copied.items.filter((i) => state.objects[i.source]) : copied.items;
    if (!items.length) return null;
    const first = items[0]!;
    const taken = Object.values(state.objects).map((o) => ({ x: o.x, y: o.y }));
    const out = items.map((i) => {
      const spot = placeSpot(state.grid, extent, { x: at.x + i.x - first.x, y: at.y + i.y - first.y }, state.scene.snap !== false, taken, true);
      taken.push(spot);
      return { source: i.source, id: crypto.randomUUID(), x: spot.x, y: spot.y };
    });
    selection.replace(out.map((i) => i.id));
    return here ? { kind: 'objects.paste', items: out } : { kind: 'objects.paste', items: out, from: copied.scene! };
  },
};

/** The same scene, or another the copy names (a DM's screen knows its scene; a seat shown no scene pastes where it copied). */
const pastesOn = (c: Copied, scene: string | null): boolean => c.scene === scene || (c.scene !== null && scene !== null);
